/**
 * Reading and writing CSV, carefully enough for a spreadsheet.
 *
 * ## Why not a library
 *
 * Because the hard parts here are not parsing — they are the two things every
 * quick implementation gets wrong and nobody notices until a customer's name
 * has a comma in it, or until a phone number arrives in Excel as 2.54712e+11.
 * Both are below, with the reason attached.
 */

/**
 * One value, escaped for a cell.
 *
 * ## The leading apostrophe
 *
 * Excel and Google Sheets read `+254712000111` as a formula, and `0712000111`
 * as the number 712000111 with the leading zero eaten. Both are the same
 * failure: a telephone number that no longer dials. Prefixing with an
 * apostrophe is the one thing both applications agree means "this is text".
 *
 * ## The other leading characters
 *
 * A cell starting `=`, `+`, `-` or `@` is executed as a formula on open. A
 * customer who types `=cmd|'/c calc'!A1` into a website form and has it land
 * in an exported spreadsheet is a real and well-documented attack, not a
 * theoretical one. The same apostrophe defuses it.
 */
function cell(value: unknown): string {
  if (value === null || value === undefined) return '';

  let text = value instanceof Date ? value.toISOString() : String(value);

  if (/^[=+\-@\t\r]/.test(text) || /^\+?\d[\d\s-]{6,}$/.test(text)) {
    text = `'${text}`;
  }

  // Quote when the value contains anything that would otherwise break a row.
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export type CsvColumn<Row> = {
  /** The heading, and the key an import matches on. */
  header: string;
  value: (row: Row) => unknown;
};

/** Rows and columns to a CSV string, headings first. */
export function toCsv<Row>(rows: Row[], columns: CsvColumn<Row>[]): string {
  const lines = [columns.map((column) => cell(column.header)).join(',')];

  for (const row of rows) {
    lines.push(columns.map((column) => cell(column.value(row))).join(','));
  }

  /*
   * CRLF, because that is what the spec says and what Excel expects. A file
   * with bare newlines opens as one long row in some Windows builds.
   */
  return lines.join('\r\n');
}

/**
 * Hand the browser a file.
 *
 * A BOM in front, which looks like superstition and is not: without it Excel
 * on Windows reads the file as the system codepage, and every name with an
 * accent or a dash in it arrives mangled. Kisii, Nyanchwa and Keroka survive
 * either way; "Kiogoro — pole 18" does not.
 */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Freed on the next tick; revoking immediately cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * A CSV string back into rows, keyed by the heading line.
 *
 * Written as a character loop rather than a split on commas, because a split
 * cannot see that a comma inside quotes is part of a value. "Ondieki Hardware,
 * Kisii" is one field and becomes two under every naive implementation.
 *
 * Returns an empty list for anything it cannot make sense of; the caller shows
 * a count and lets somebody look before committing.
 */
export function parseCsv(text: string): Array<Record<string, string>> {
  // Strip the BOM we or Excel may have written.
  const input = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];

  let row: string[] = [];
  let value = '';
  let quoted = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i]!;

    if (quoted) {
      if (char === '"') {
        // A doubled quote inside a quoted field is one literal quote.
        if (input[i + 1] === '"') {
          value += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        value += char;
      }
      continue;
    }

    if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(value);
      value = '';
    } else if (char === '\n' || char === '\r') {
      // Consume the pair rather than emitting a blank row between them.
      if (char === '\r' && input[i + 1] === '\n') i += 1;
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
    } else {
      value += char;
    }
  }

  // Whatever is left when the file does not end in a newline.
  if (value !== '' || row.length > 0) {
    row.push(value);
    rows.push(row);
  }

  const [headings, ...body] = rows.filter((one) => one.some((field) => field.trim() !== ''));
  if (!headings) return [];

  /*
   * The apostrophe that protected a phone number on the way out is removed on
   * the way back in. Round-tripping a file through this system must not
   * accumulate punctuation.
   */
  const clean = (field: string): string => field.replace(/^'/, '').trim();

  return body.map((line) => {
    const record: Record<string, string> = {};
    headings.forEach((heading, index) => {
      record[clean(heading)] = clean(line[index] ?? '');
    });
    return record;
  });
}
